/**
 * TypeScript view of the `bridges` (hashiwokakero) format.
 * Source of truth: `shared/schema/bridges.{definition,submission,solution,progress}.json`.
 *
 * The grid is one string per row, `1`–`8` an island and `.` water. A bridge joins two
 * neighbouring islands on a row or a column, `a` first in reading order, `n` parallel bridges.
 */

export type Localized = Record<string, string | undefined>;

export interface BridgesDefinition {
  puzzleType: "bridges";
  typeVersion: number;
  rows: number;
  cols: number;
  /** One string per row: a digit 1-8 for an island, `.` for water. */
  islands: string[];
  rulesText?: Localized;
  meta?: Record<string, unknown>;
}

export const WATER = ".";

export type Cell = [row: number, col: number];

export interface Bridge {
  a: Cell;
  b: Cell;
  n: 1 | 2;
}

/** Autosave shape. Opaque to the core; this type owns it and its migrations. */
export interface BridgesProgress {
  version: 1;
  bridges: Bridge[];
}

export interface BridgesSubmission {
  bridges: Bridge[];
}

/** The author's bridges, in the submission format. */
export type BridgesSolution = BridgesSubmission;

export const MIN_SIZE = 2;
export const MAX_SIZE = 30;

export function cellKey([r, c]: Cell): string {
  return `${r},${c}`;
}

export function cellId(row: number, col: number): string {
  return `r${row}c${col}`;
}

/** Identifies the pair of islands a bridge joins, whatever the order of its ends. */
export function pairKey(a: Cell, b: Cell): string {
  const [first, second] = before(a, b) ? [a, b] : [b, a];
  return `${cellKey(first)}-${cellKey(second)}`;
}

/** Whether `a` comes before `b` in reading order. */
export function before(a: Cell, b: Cell): boolean {
  return a[0] < b[0] || (a[0] === b[0] && a[1] < b[1]);
}

/** Picks the viewer's language, falling back to the base language, then English, then any. */
export function localized(value: Localized | null | undefined, locale: string): string {
  if (!value) return "";
  return (
    value[locale] ??
    value[locale.split("-")[0]] ??
    value.en ??
    Object.values(value).find((v) => typeof v === "string" && v.length > 0) ??
    ""
  );
}
